import { measureLatency } from '../../gctl-measure';
import {
  NetworkError,
  deepCamelCase,
  Select,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../fetchers/_helpers';
import {
  PrivateCommentsFields,
  PrivateComments,
} from '../../models/components/schemas/private-comments/PrivateComments';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type DeleteVideoPrivateCommentFields = readonly string[];

/** Options for DELETE /videos/{video_id}/private_comments/{comment_id} */
export type DeleteVideoPrivateCommentOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the private comment.
     */
    commentId: number;
  };

  /** Possible query parameters for /videos/{video_id}/private_comments/{comment_id} */
  query?: {
    /**
     * The password for the review link.
     */
    password?: string;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;
  };
};

/** Result type for DELETE /videos/{video_id}/private_comments/{comment_id} */
export type DeleteVideoPrivateCommentResult<Paths extends readonly string[]> = unknown;

/**
 * Delete a private comment
 * This method deletes the specified private comment. The authenticated user must be the owner of either the comment or the video.
 */
export async function deleteVideoPrivateComment<Paths extends DeleteVideoPrivateCommentFields>({
  baseUrl,
  where: { videoId, commentId },
  query,
  ...options
}: DeleteVideoPrivateCommentOptions<Paths>): Promise<DeleteVideoPrivateCommentResult<Paths>> {
  return measureLatency('deleteVideoPrivateComment', 'DELETE', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/private_comments/${commentId}?${searchQueryString(query)}`,
      {
        ...options,
        method: 'DELETE',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as DeleteVideoPrivateCommentResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PatchVideoPrivateCommentFields = readonly PrivateCommentsFields[];

/** Options for PATCH /videos/{video_id}/private_comments/{comment_id} */
export type PatchVideoPrivateCommentOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the private comment.
     */
    commentId: number;
  };

  /** Possible query parameters for /videos/{video_id}/private_comments/{comment_id} */
  query?: {
    /**
     * The password for the review link.
     */
    password?: string;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;
  };

  /** Data serialized to body for PATCH /videos/{video_id}/private_comments/{comment_id} */
  variables?: {
    coordinates?: {
      /**
       * The X coordinate of the private comment's position. This value is expressed as a proportion of the video's width in pixels.
       * @example 0.5
       */

      x?: number;

      /**
       * The Y coordinate of the private comment's position. This value is expressed as a proportion of the video's width in pixels.
       * @example 0.75
       */

      y?: number;
    };

    /**
     * The rich comment in JSON stringified form.
     * @example {"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":"Hi Tom"}]}]}
     */

    richtext?: string;

    /**
     * The status of the private comment.
     *
     * Option descriptions:
     *  * `closed` - The private comment is closed.
     *  * `open` - The private comment is open.
     *
     */

    status?: `closed` | `open`;

    /**
     * The comment text.
     * @example This scene can be removed
     */

    text?: string;

    /**
     * The time in seconds when the private comment appears in the video.
     * @example 300
     */

    timeCode?: number;
  };
};

/** Result type for PATCH /videos/{video_id}/private_comments/{comment_id} */
export type PatchVideoPrivateCommentResult<Paths extends readonly string[]> = Select<
  PrivateComments,
  Paths
>;

/**
 * Edit a private comment
 * This method edits the specified private comment. The authenticated user must be the owner of the comment.
 */
export async function patchVideoPrivateComment<Paths extends PatchVideoPrivateCommentFields>({
  baseUrl,
  select,
  variables,
  where: { videoId, commentId },
  query,
  ...options
}: PatchVideoPrivateCommentOptions<Paths>): Promise<PatchVideoPrivateCommentResult<Paths>> {
  return measureLatency('patchVideoPrivateComment', 'PATCH', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/private_comments/${commentId}?${searchQueryString(
        query,
      )}&fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'PATCH',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PatchVideoPrivateCommentResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
